import { cn } from '@/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { HTMLAttributes, PropsWithChildren } from 'react';

/*
 * The public site's content container.
 *
 * It used to own padding but not width, so every page invented its own
 * constraint — `lg:mx-32`, `lg:mx-16 xl:mx-32`, `lg:w-5/6`, `max-w-5xl` and
 * `max-w-lg` all appeared for the same job. The `width` variant replaces them.
 *
 * SectionLayout constrains content. To paint a full-bleed background behind it,
 * wrap it in <Band>.
 */
const sectionVariants = cva('w-full flex flex-col', {
  variants: {
    width: {
      /** Long-form reading: legal pages, policy text. */
      prose: 'mx-auto max-w-3xl px-5 md:px-8',
      /** The site default. */
      default: 'mx-auto max-w-7xl px-5 md:px-8 lg:px-12',
      /** Galleries and product grids that want more room. */
      wide: 'mx-auto max-w-360 px-5 md:px-8 lg:px-12',
      /** Edge-to-edge: heroes, banners, full-bleed imagery. */
      full: '',
    },
    space: {
      none: 'py-0 gap-y-0',
      tight: 'py-6 gap-y-4',
      default: 'py-6 gap-y-6',
      lg: 'py-12 md:py-20 gap-y-8 md:gap-y-10',
    },
  },
  defaultVariants: {
    width: 'default',
    space: 'default',
  },
});

type SectionLayoutProps = PropsWithChildren<HTMLAttributes<HTMLDivElement>> &
  VariantProps<typeof sectionVariants>;

const SectionLayout = ({
  children,
  className,
  width,
  space,
  ...props
}: SectionLayoutProps) => {
  return (
    <section
      className={cn(sectionVariants({ width, space }), className)}
      {...props}
    >
      {children}
    </section>
  );
};

export default SectionLayout;
